import { useState, forwardRef, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  Slide,
  DialogContent,
  SlideProps,
  DialogActions,
  Paper,
  Stack,
  InputAdornment,
} from "@mui/material";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import LockResetRounded from "@mui/icons-material/LockResetRounded";
import LockOutlined from "@mui/icons-material/LockOutlined";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import { useAuthStore } from "../store/authStore";
import { WarningAmber } from "@mui/icons-material";
import { changePassword } from "../utils/api";
import { CustomSnackbar, PageHeader } from "../components";
import TwoFactorSection from "../components/TwoFactorSection";
import TwoFactorAdminReset from "../components/TwoFactorAdminReset";
import { CustomSnackbarRef } from "../components/CustomSnackbar";

const Transition = forwardRef<HTMLDivElement, SlideProps>((props, ref) => (
  <Slide direction="up" timeout={350} ref={ref} {...props} />
));

const SettingsPage = () => {
  const snackbarRef = useRef<CustomSnackbarRef>(null);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [open, setOpen] = useState(false);
  const { logout, user } = useAuthStore();
  // /me отдаёт { username, role }; сброс второго фактора доступен только админу.
  // В preview сессии нет, поэтому блок показываем — как и остальной интерфейс,
  // который там открыт в обход ProtectedRoute.
  const isAdmin =
    process.env.REACT_APP_UI_PREVIEW === "true" ||
    (user as any)?.role === "admin";

  const handleChangePassword = async () => {
    setError(null);
    setSuccess(false);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("Пожалуйста, заполните все поля.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Новый пароль и подтверждение не совпадают.");
      return;
    }

    try {
      await changePassword({
        old_password: oldPassword,
        new_password: newPassword,
      });

      setSuccess(true);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      snackbarRef.current?.showSnackbar("Пароль изменён", "success");
    } catch (requestError) {
      setError("Не удалось изменить пароль.");
      console.error("Error:", requestError);
      snackbarRef.current?.showSnackbar("Ошибка при смене пароля", "error");
    }
  };

  const passwordAdornment = {
    startAdornment: (
      <InputAdornment position="start">
        <LockOutlined color="action" fontSize="small" />
      </InputAdornment>
    ),
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "calc(var(--content-max-height) + 22dvh)",
        overflowY: "auto",
        p: { xs: 2, sm: 3, lg: 4 },
      }}
    >
      <CustomSnackbar duration={1600} ref={snackbarRef} />

      <Box sx={{ maxWidth: 920, mx: "auto" }}>
        <PageHeader
          title="Настройки"
          description="Безопасность учётной записи и завершение сеанса"
          icon={ShieldOutlined}
        />

        <TwoFactorSection
          onNotify={(message, severity) =>
            snackbarRef.current?.showSnackbar(message, severity)
          }
        />

        {isAdmin && (
          <TwoFactorAdminReset
            onNotify={(message, severity) =>
              snackbarRef.current?.showSnackbar(message, severity)
            }
          />
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.5fr) minmax(260px, .8fr)" },
            gap: 2.5,
          }}
        >
          <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: "20px" }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
              <LockResetRounded color="primary" />
              <Typography variant="h6">Смена пароля</Typography>
            </Stack>
            <Typography color="text.secondary" fontSize={14} sx={{ mb: 3 }}>
              Используйте уникальный пароль, который не применяется в других
              сервисах.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2.5 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2.5 }}>
                Пароль успешно изменён.
              </Alert>
            )}

            <Stack spacing={2.25}>
              <TextField
                label="Текущий пароль"
                type="password"
                fullWidth
                value={oldPassword}
                onChange={(event) => setOldPassword(event.target.value)}
                slotProps={{ input: passwordAdornment }}
              />
              <TextField
                label="Новый пароль"
                type="password"
                fullWidth
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                slotProps={{ input: passwordAdornment }}
              />
              <TextField
                label="Подтвердите новый пароль"
                type="password"
                fullWidth
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                slotProps={{ input: passwordAdornment }}
              />
            </Stack>

            <Button
              onClick={handleChangePassword}
              variant="contained"
              startIcon={<LockResetRounded />}
              sx={{ mt: 3 }}
            >
              Обновить пароль
            </Button>
          </Paper>

          <Paper
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: "20px",
              alignSelf: "start",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                display: "grid",
                placeItems: "center",
                borderRadius: "13px",
                color: "error.main",
                bgcolor: "rgba(229, 72, 77, 0.10)",
                mb: 2,
              }}
            >
              <LogoutRounded />
            </Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Завершить сеанс
            </Typography>
            <Typography color="text.secondary" fontSize={14} sx={{ mb: 3 }}>
              Для повторного входа потребуется логин и пароль.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<LogoutRounded />}
              onClick={() => setOpen(true)}
            >
              Выйти из аккаунта
            </Button>
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={open}
        disableScrollLock
        onClose={() => setOpen(false)}
        slots={{ transition: Transition }}
        disableEnforceFocus
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Выйти из аккаунта?</DialogTitle>
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <WarningAmber color="warning" />
            <Typography color="text.secondary">
              Текущий сеанс будет завершён.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setOpen(false)} color="inherit">
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DisabledByDefaultIcon />}
            onClick={logout}
          >
            Выйти
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
